import { useTranslations } from "@/lib/useTranslations";
import { useActionState, useEffect, useState } from "react";
import { useRefresh } from "@/hooks/useRefresh";

import { defaultLocale } from "@/i18n/config";

import { createTeamMember, resendTeamMemberInvite, updateTeamMember } from "@/api/team";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import type {
  CreateTeamMemberState,
  TeamMember,
  UpdateTeamMemberState,
} from "@/core/services/types/team.types";

const initialCreateTeamMemberState: CreateTeamMemberState = {
  success: false,
};

const initialUpdateTeamMemberState: UpdateTeamMemberState = {
  success: false,
};

const inputClassName =
  "w-full wireframe-border bg-whart-bg px-3 py-2 text-sm text-whart-text outline-none focus:border-whart-dark disabled:opacity-50";

function formatFullName(member: TeamMember): string {
  return `${member.first_name} ${member.last_name}`.trim();
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat(defaultLocale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;

  return <p className="text-xs text-whart-urgent">{message}</p>;
}

interface TeamMemberDialogProps {
  open: boolean;
  member: TeamMember | null;
  onClose: () => void;
  onMemberCreated: (member: TeamMember) => void;
  onMemberUpdated: (member: TeamMember) => void;
}

function InviteLinkPanel({
  inviteUrl,
  onClose,
}: {
  inviteUrl: string;
  onClose: () => void;
}) {
  const t = useTranslations("team");
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 text-sm text-amber-950">
        <p className="font-medium">{t("inviteLinkTitle")}</p>
        <p className="mt-1 text-amber-900">{t("inviteLinkDescription")}</p>
      </div>

      <div className="space-y-2">
        <label htmlFor="invite_url" className="text-sm font-medium text-whart-text">
          URL
        </label>
        <input
          id="invite_url"
          type="text"
          readOnly
          value={inviteUrl}
          className={`${inputClassName} font-mono text-xs`}
          onFocus={(event) => event.currentTarget.select()}
        />
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="secondary" onClick={handleCopy}>
          {copied ? t("inviteLinkCopied") : t("copyInviteLink")}
        </Button>
        <Button type="button" onClick={onClose}>
          {t("closeAfterInviteLink")}
        </Button>
      </div>
    </div>
  );
}

function TeamMemberCreateForm({
  onClose,
  onMemberCreated,
}: {
  onClose: () => void;
  onMemberCreated: (member: TeamMember) => void;
}) {
  const t = useTranslations("team");
  const tCommon = useTranslations("common");
  const tRoles = useTranslations("team.roles");
  const refresh = useRefresh();
  const [pendingInvite, setPendingInvite] = useState<{
    url: string;
    member: TeamMember;
  } | null>(null);
  const [state, formAction, isPending] = useActionState(
    createTeamMember,
    initialCreateTeamMemberState,
  );

  useEffect(() => {
    if (!state.success || !state.data) {
      return;
    }

    if (state.inviteUrl) {
      setPendingInvite({ url: state.inviteUrl, member: state.data });
      return;
    }

    onMemberCreated(state.data);
    refresh();
    onClose();
  }, [state, onMemberCreated, onClose, refresh]);

  if (pendingInvite) {
    return (
      <InviteLinkPanel
        inviteUrl={pendingInvite.url}
        onClose={() => {
          onMemberCreated(pendingInvite.member);
          setPendingInvite(null);
          refresh();
          onClose();
        }}
      />
    );
  }

  const fieldErrors = state.success ? undefined : state.fieldErrors;

  return (
    <form action={formAction} className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <label htmlFor="first_name" className="text-sm font-medium text-whart-text">
            {t("fields.firstName")}
          </label>
          <input
            id="first_name"
            name="first_name"
            type="text"
            required
            disabled={isPending}
            className={inputClassName}
          />
          <FieldError message={fieldErrors?.first_name} />
        </div>

        <div className="space-y-2">
          <label htmlFor="last_name" className="text-sm font-medium text-whart-text">
            {t("fields.lastName")}
          </label>
          <input
            id="last_name"
            name="last_name"
            type="text"
            required
            disabled={isPending}
            className={inputClassName}
          />
          <FieldError message={fieldErrors?.last_name} />
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="email" className="text-sm font-medium text-whart-text">
          {tCommon("email")}
        </label>
        <input
          id="email"
          name="email"
          type="email"
          required
          disabled={isPending}
          className={inputClassName}
        />
        <FieldError message={fieldErrors?.email} />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <label htmlFor="role" className="text-sm font-medium text-whart-text">
            {tCommon("role")}
          </label>
          <select
            id="role"
            name="role"
            required
            disabled={isPending}
            defaultValue="EDITOR"
            className={inputClassName}
          >
            <option value="ADMIN">{tRoles("admin")}</option>
            <option value="EDITOR">{tRoles("editor")}</option>
          </select>
          <FieldError message={fieldErrors?.role} />
        </div>

        <div className="space-y-2">
          <label htmlFor="is_active" className="text-sm font-medium text-whart-text">
            {tCommon("status")}
          </label>
          <select
            id="is_active"
            name="is_active"
            required
            disabled={isPending}
            defaultValue="true"
            className={inputClassName}
          >
            <option value="true">{tCommon("active")}</option>
            <option value="false">{tCommon("inactive")}</option>
          </select>
          <FieldError message={fieldErrors?.is_active} />
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="avatar_url" className="text-sm font-medium text-whart-text">
          {t("fields.avatarUrl")}
        </label>
        <input
          id="avatar_url"
          name="avatar_url"
          type="url"
          disabled={isPending}
          placeholder={t("fields.avatarPlaceholder")}
          className={inputClassName}
        />
        <FieldError message={fieldErrors?.avatar_url} />
      </div>

      <p className="rounded-xl border border-black/10 bg-zinc-50 px-3 py-2 text-sm text-neutral-700">
        {t("inviteNotice")}
      </p>

      {!state.success && state.error ? (
        <p
          role="alert"
          className="wireframe-border px-3 py-2 text-sm text-whart-urgent"
        >
          {state.error}
        </p>
      ) : null}

      <div className="flex justify-end gap-2 pt-1">
        <Button
          type="button"
          variant="secondary"
          onClick={onClose}
          disabled={isPending}
        >
          {tCommon("cancel")}
        </Button>
        <Button type="submit" disabled={isPending}>
          {isPending ? t("creating") : t("create")}
        </Button>
      </div>
    </form>
  );
}

function TeamMemberEditForm({
  member,
  onClose,
  onMemberUpdated,
}: {
  member: TeamMember;
  onClose: () => void;
  onMemberUpdated: (member: TeamMember) => void;
}) {
  const t = useTranslations("team");
  const tCommon = useTranslations("common");
  const tRoles = useTranslations("team.roles");
  const refresh = useRefresh();
  const [pendingInviteUrl, setPendingInviteUrl] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [state, formAction, isPending] = useActionState(
    updateTeamMember,
    initialUpdateTeamMemberState,
  );

  useEffect(() => {
    if (state.success && state.data) {
      onMemberUpdated(state.data);
      refresh();
      onClose();
    }
  }, [state, onMemberUpdated, onClose, refresh]);

  const handleResendInvite = async () => {
    setIsResending(true);
    setResendError(null);
    setResendSuccess(null);
    setPendingInviteUrl(null);

    const result = await resendTeamMemberInvite(member.email);

    setIsResending(false);

    if (!result.success) {
      setResendError(result.error ?? t("resendInviteFailed"));
      return;
    }

    if (result.inviteUrl) {
      setPendingInviteUrl(result.inviteUrl);
      return;
    }

    setResendSuccess(t("inviteResent"));
  };

  if (pendingInviteUrl) {
    return (
      <InviteLinkPanel
        inviteUrl={pendingInviteUrl}
        onClose={() => {
          setPendingInviteUrl(null);
        }}
      />
    );
  }

  const fieldErrors = state.success ? undefined : state.fieldErrors;

  return (
    <form action={formAction} className="space-y-5">
          <input type="hidden" name="id" value={member.id} />

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label
                htmlFor="first_name"
                className="text-sm font-medium text-whart-text"
              >
                {t("fields.firstName")}
              </label>
              <input
                id="first_name"
                name="first_name"
                type="text"
                required
                disabled={isPending}
                defaultValue={member.first_name}
                className={inputClassName}
              />
              <FieldError message={fieldErrors?.first_name} />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="last_name"
                className="text-sm font-medium text-whart-text"
              >
                {t("fields.lastName")}
              </label>
              <input
                id="last_name"
                name="last_name"
                type="text"
                required
                disabled={isPending}
                defaultValue={member.last_name}
                className={inputClassName}
              />
              <FieldError message={fieldErrors?.last_name} />
            </div>
          </div>

          <div className="space-y-2">
            <label htmlFor="email" className="text-sm font-medium text-whart-text">
              {tCommon("email")}
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              disabled={isPending}
              defaultValue={member.email}
              className={inputClassName}
            />
            <FieldError message={fieldErrors?.email} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label htmlFor="role" className="text-sm font-medium text-whart-text">
                {tCommon("role")}
              </label>
              <select
                id="role"
                name="role"
                required
                disabled={isPending}
                defaultValue={member.role}
                className={inputClassName}
              >
                <option value="ADMIN">{tRoles("admin")}</option>
                <option value="EDITOR">{tRoles("editor")}</option>
              </select>
              <FieldError message={fieldErrors?.role} />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="is_active"
                className="text-sm font-medium text-whart-text"
              >
                {tCommon("status")}
              </label>
              <select
                id="is_active"
                name="is_active"
                required
                disabled={isPending}
                defaultValue={member.is_active ? "true" : "false"}
                className={inputClassName}
              >
                <option value="true">{tCommon("active")}</option>
                <option value="false">{tCommon("inactive")}</option>
              </select>
              <FieldError message={fieldErrors?.is_active} />
            </div>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="avatar_url"
              className="text-sm font-medium text-whart-text"
            >
              {t("fields.avatarUrl")}
            </label>
            <input
              id="avatar_url"
              name="avatar_url"
              type="url"
              disabled={isPending}
              defaultValue={member.avatar_url ?? ""}
              placeholder={t("fields.avatarPlaceholder")}
              className={inputClassName}
            />
            <FieldError message={fieldErrors?.avatar_url} />
          </div>

          <div className="grid grid-cols-[7rem_1fr] gap-x-4 border-t border-neutral-100 pt-4 text-sm">
            <span className="text-neutral-500">{tCommon("created")}</span>
            <span className="text-whart-text">{formatDate(member.created_at)}</span>
            <span className="text-neutral-500">{tCommon("updated")}</span>
            <span className="text-whart-text">{formatDate(member.updated_at)}</span>
          </div>

          {member.is_active ? (
            <div className="space-y-3 border-t border-neutral-100 pt-4">
              <p className="text-sm text-neutral-600">{t("resendInviteDescription")}</p>
              <Button
                type="button"
                variant="secondary"
                disabled={isPending || isResending}
                onClick={() => void handleResendInvite()}
              >
                {isResending ? t("resendingInvite") : t("resendInvite")}
              </Button>
              {resendSuccess ? (
                <p className="text-sm text-emerald-700">{resendSuccess}</p>
              ) : null}
              {resendError ? (
                <p role="alert" className="text-sm text-whart-urgent">
                  {resendError}
                </p>
              ) : null}
            </div>
          ) : null}

          {!state.success && state.error ? (
            <p
              role="alert"
              className="wireframe-border px-3 py-2 text-sm text-whart-urgent"
            >
              {state.error}
            </p>
          ) : null}

          <div className="flex justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isPending}
            >
              {tCommon("cancel")}
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? tCommon("saving") : tCommon("saveChanges")}
            </Button>
          </div>
        </form>
  );
}

export function TeamMemberDialog({
  open,
  member,
  onClose,
  onMemberCreated,
  onMemberUpdated,
}: TeamMemberDialogProps) {
  const t = useTranslations("team.dialog");
  const isEditMode = member !== null;
  const editTitle = member
    ? t("editTitle", { name: formatFullName(member) })
    : t("title");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEditMode ? editTitle : t("createTitle")}
      panelClassName="max-w-lg"
    >
      {isEditMode && member ? (
        <TeamMemberEditForm
          key={member.id}
          member={member}
          onClose={onClose}
          onMemberUpdated={onMemberUpdated}
        />
      ) : (
        <TeamMemberCreateForm onClose={onClose} onMemberCreated={onMemberCreated} />
      )}
    </Dialog>
  );
}
